/* eslint-disable @typescript-eslint/no-unused-vars */
import { AxiosError } from 'axios';
import { formatLocale } from 'd3-format';
import * as ExcelJS from 'exceljs';
import {
	AdminMasterData,
	AdminMaterialListEntries,
	IAdminBackpackMasterData,
	IAllMaterialType,
	IAllNewsCategoriesList,
	IAllNewsType,
	IApiMasterData,
	IBackpackForecastList,
	IBackpackNewsList,
	IBackpackPdfModuleList,
	IBackpackUserList,
	IBakcpackMaterialList,
	IForecastGroupData,
	IForecastsListType,
	IGroupUserListDownload,
	IMaterialGroupData,
	IMatfoxxJokesList,
	IMatfoxxOffersList,
	INewsGroupData,
	IAllCostModelType,
	IPdfModuleGroupData,
	IPdfModuleListType
} from 'features/admin/interface/admin';
import { CostModelGroupData, handleNameKey } from 'features/admin/util/utility';
import { IDashboardData } from 'features/dashboard/interface/dashboard';
import { IChartInformation, IChartItem, IChartSettingsType, IExcelHeader } from 'features/futuretable/interface/futureTable';
import { IAdminSearchMaterialCourseList, ICSVData, ICourseList, IExportData } from 'features/material/interface/material';
import { saveAs } from 'file-saver';
import i18n, { TFunction, t } from 'i18next';
import { filter, isEmpty, round } from 'lodash';
import moment from 'moment';
import {
	DateRangeMapper,
	adminAdvancedSearchCsvHeaders,
	adminListEntriesHeader,
	adminMasterDataHeaders,
	apiMasterDataCsvHeaders,
	apiMasterDataNewsCsvHeaders,
	chartUpdateFrequencyMapper,
	customCsvHeaders,
	forecastBackpackMasterDataHeaders,
	getDifferenceDaysForOneYear,
	groupUserListHeader,
	matfoxxJokesListHeaders,
	matfoxxOffersListHeaders,
	newsBackpackMasterDataHeaders,
	newsListHeaders,
	pdfModuleBackpackMasterDataHeaders,
	userListBackpackMasterDataHeaders
} from 'shared/constants/constants';
import {
	AddInfoIcon,
	AddedOnIcon,
	CategotyIcon,
	CurrencyUnitIcon,
	DataAvailabilityIcon,
	EndedOnIcon,
	FrequencyIcon,
	NameIcon,
	SourceIcon,
	StockExchangeIcon
} from 'shared/icons/icon';
import { ChartAxisPosition, ForeCastType } from 'shared/interface/enums';
import { Action, IDropDownOption, State } from '../interface';
import localizationConstants from './translation/constants';
import { notify } from 'shared/components/notification/notification';
import store from 'store';
import { IChartData } from 'features/futuretable/interface/futureTable';

export const createAction = (ACTION: string, data: any = null): Action => ({
	type: ACTION,
	payload: data
});

export const createLoadingSelector = (actions: string[]) => (state: State) => {
	// returns true only when all actions is not loading
	let loader = false;
	for (let i = 0; i < actions.length; i += 1) {
		if (state.loading.api[actions[i]]) {
			loader = true;
			break;
		}
	}
	return loader;
};

/**
 * function which returns formatted date
 * @param date
 */
export const formatDate = (date: any, format?: string) => {
	if (!date) {
		return '';
	}
	return moment(date)
		.local()
		.format(format?.toUpperCase() || 'DD-MM-YYYY');
};

export const DateFormat = (date: any, format: string) => {
	if (!date) {
		return '';
	}
	return moment(date, 'DD.MM.YYYY').local().format(format?.toUpperCase());
};
export const capitalizeFirstLetter = (string: string) => string && string.charAt(0).toUpperCase() + string.slice(1);

export const handleError = (error: AxiosError) => {
	const isError = error.response && error.response.data;
	if (isError) {
		return error.response?.data.message;
	}
	return 'Something went wrong';
};

export const debounce = <T extends (...args: any[]) => void>(func: T, wait = 400) => {
	let timeout: NodeJS.Timeout | null = null;

	const debounced = (...args: Parameters<T>) => {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(() => func(...args), wait);
	};

	debounced.cancel = () => {
		if (timeout) {
			clearTimeout(timeout);
			timeout = null;
		}
	};

	return debounced as T & { cancel: () => void };
};

export const customNumberFormatter = (value: number | any, maxFractionLimit?: number, separator?: string) => {
	if (value === null || value === undefined || isNaN(value)) {
		return '';
	}

	const numberValue = typeof value === 'string' ? parseFloat(value) : value;

	if (isNaN(numberValue)) {
		return '';
	}

	// Get decimal_separator from siteConfig state
	const state = store.getState();
	const decimalSeparator = (separator && separator) || state.siteConfig.decimal_separator;
	const localeConfig = decimalSeparator === 'de' ? 'de-DE' : 'en-US';

	const fractionLimit = maxFractionLimit || 2;

	// 👇 Truncate the number to specified fraction digits
	const multiplier = Math.pow(10, fractionLimit);
	const roundedValue = Math.round(numberValue * multiplier) / multiplier;

	return new Intl.NumberFormat(localeConfig, {
		style: 'decimal',
		minimumFractionDigits: 0,
		maximumFractionDigits: fractionLimit,
		useGrouping: true
	}).format(roundedValue);
};

export const windowScroll = (value: number, position: 'top' | 'bottom' = 'top', behavior: 'auto' | 'smooth' = 'smooth') =>
	window.scrollTo({
		[position]: value,
		behavior: behavior
	});

export const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

export const getCurrentTime = () => {
	const currentTime = new Date().getMinutes() * 60000 + new Date().getHours() * 3600000;
	return currentTime;
};

export const onSetFiles = (files: File | null, type: string, field: string, setFieldValue: (field: string, value: any) => void) => {
	if (files) {
		const fileName = files ? files.name : '';
		const data = {
			file: files || ({} as File),
			name: fileName,
			type: files.type,
			url: URL.createObjectURL(files)
		};
		setFieldValue(field, data);
	}
};

export const convertDropDownObjectToValueArray = (dropDownObject: IDropDownOption[]) => dropDownObject.map((item) => item.value);

export const exportMaterialCourseList = async (
	data: IExportData[],
	selectedDateRange: string,
	language: string,
	t: TFunction<'translation', undefined, 'translation'>
) => {
	const splittedValue = data[0].datum?.split('.');
	const date = splittedValue ? new Date(`${splittedValue[1]}/${splittedValue[0]}/${splittedValue[2]}`) : null;
	const histDatum = date ? moment(date).subtract(DateRangeMapper[selectedDateRange][0], DateRangeMapper[selectedDateRange][1]).format() : null;
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Kursliste');
	const sheetName = `${data[0].listen_id}-1-${language === 'en' && data[0].listen_name_eng !== '' ? data[0].listen_name_eng : data[0].listen_name}`;
	worksheet.columns = customCsvHeaders(t);
	data.forEach((course: ICourseList) => {
		worksheet.addRow({
			name: (language === 'en' ? course.name_eng : course.name) || '-',
			faelligkeit_f: course.faelligkeit_f,
			datum: course.datum,
			hauptkurs: course.disallowed !== 1 && course.hauptkurs !== null ? round(Number(course.hauptkurs), course.nkstellen) : null,
			hist_kurs: course.disallowed !== 1 && course.hist_kurs !== null ? round(Number(course.hist_kurs), course.nkstellen) : null,
			hist_perf: course.disallowed !== 1 && course.hist_perf !== null ? round(Number(course.hist_perf), course.nkstellen) : null,
			hist_hoch: course.disallowed !== 1 && course.hist_hoch !== null ? round(Number(course.hist_hoch), course.nkstellen) : null,
			hist_tief: course.disallowed !== 1 && course.hist_tief !== null ? round(Number(course.hist_tief), course.nkstellen) : null,
			waehrung: course.waehrung,
			einheit: course.einheit,
			histDatum: histDatum ? formatDate(histDatum, 'DD.MM.YYYY') : ''
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Kursliste--${sheetName.replaceAll(' ', '_')}--${formatDate(new Date(), 'YYYY-MM-DD')}--${
		moment().hour() < 10 ? '0' + moment().hour() : moment().hour()
	}-${moment().minute() < 10 ? '0' + moment().minute() : moment().minute()}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportGroupUserList = async (data: IGroupUserListDownload[], t: TFunction<'translation', undefined, 'translation'>) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Kursliste');
	worksheet.columns = groupUserListHeader(t);
	data.forEach((item: IGroupUserListDownload) => {
		worksheet.addRow({
			group: item.groupName || '',
			user_id: item.benutzer_id || '',
			email: item.email || '',
			code: item.promotionalCode || ''
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	saveAs(new Blob([buf]), `Gruppen-Nutzerliste.xlsx`);
};

export const swapData = (data: IDashboardData[], oldIndex: number, newIndex: number) => {
	const itemsCopy = [...data];
	const oldIndexBackup = itemsCopy[oldIndex];
	itemsCopy[oldIndex] = itemsCopy[newIndex];
	itemsCopy[newIndex] = oldIndexBackup;
	return [...itemsCopy];
};

export const onSwapBetweenList = (oldIndex: number, newIndex: number, arrayOfPullData: IDashboardData[], arrayOfPutData: IDashboardData[]) => {
	const copyOfPullData = [...arrayOfPullData];
	const copyOfPutData = [...arrayOfPutData];
	if (copyOfPutData.length === 0) {
		copyOfPutData.push(copyOfPullData[oldIndex as number]);
		copyOfPullData.splice(oldIndex as number, 1);
		return { copyOfPullData: copyOfPullData, copyOfPutData: copyOfPutData };
	} else {
		if (newIndex !== 7) {
			if (arrayOfPutData[newIndex as number]) {
				copyOfPullData.splice(oldIndex as number, 1, arrayOfPutData[newIndex as number]);
			} else {
				copyOfPullData.splice(oldIndex as number, 1);
			}
			copyOfPutData.splice(newIndex as number, 1, arrayOfPullData[oldIndex as number]);
		}
		return { copyOfPullData: copyOfPullData, copyOfPutData: copyOfPutData };
	}
};

export const getNumberWithoutSpecialCharacters = (number: string) => {
	if (number.split('')[0] === '-') {
		return number.replaceAll('-', '').split('%')[0];
	} else {
		return number.split(' ')[0];
	}
};

export const exportAdminMaterialCourseList = async (data: IAdminSearchMaterialCourseList[]) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Stammdaten--Suche');
	worksheet.columns = adminAdvancedSearchCsvHeaders;
	data.forEach((course: IAdminSearchMaterialCourseList) => {
		worksheet.addRow({
			basis_symbol: course.basis_symbol,
			boerse: course.boerse,
			symbol: course.symbol,
			isin: course.isin || '-',
			editierbar_: course.editierbar_,
			land: course.land,
			waehrung: course.waehrung,
			waehrung_ignore: course.waehrung_ignore,
			einheit: course.einheit,
			notierung: course.notierung,
			notierung_zusatz: course.notierung_zusatz,
			nkstellen: course.nkstellen,
			max_kursalter: course.max_kursalter,
			name_deu: course.name_deu,
			name_eng: course.name_eng,
			boersenname: course.boersenname,
			datenlieferant: course.datenlieferant,
			quelle: course.quelle,
			kunde: course.kunde,
			faelligkeit: course.faelligkeit,
			faelligkeit_f: course.faelligkeit_f,
			frei: course.frei,
			sichtbar: course.sichtbar || 0,
			name_zusatz: course.name_zusatz || '-',
			hinzugefuegt_am: course.hinzugefuegt_am,
			entfernt_am: course.entfernt_am,
			lagerbestand_symbol: course.lagerbestand_symbol,
			lagerbestand_boerse: course.lagerbestand_boerse,
			info_de: course.info_de,
			info_en: course.info_en,
			priority: course.priority,
			verantwortlich: course.verantwortlich,
			prozent_abweichung_erlaubt: course.prozent_abweichung_erlaubt,
			aid_kommentar_: course.aid_kommentar_,
			link: course.link,
			notierung_zusatz_en: course.notierung_zusatz_en
		});
	});

	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Stammdaten--Suche--${formatDate(new Date(), 'YYYY-MM-DD')}--${
		moment().hour() < 10 ? '0' + moment().hour() : moment().hour()
	}-${moment().minute() < 10 ? '0' + moment().minute() : moment().minute()}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportApiMasterData = async (data: IApiMasterData, userId: number) => {
	const { lists, news } = data;
	const workbook = new ExcelJS.Workbook();
	const courserWorksheet = workbook.addWorksheet('Courses');
	const newsWorkSheet = workbook.addWorksheet('News');
	courserWorksheet.columns = apiMasterDataCsvHeaders;
	newsWorkSheet.columns = apiMasterDataNewsCsvHeaders;
	lists.forEach(
		({
			symbol,
			boerse,
			basis_symbol,
			isin,
			faelligkeit,
			faelligkeit_f,
			sichtbar,
			land,
			waehrung,
			einheit,
			notierung,
			notierung_zusatz,
			name_deu,
			name_eng,
			name_zusatz,
			quelle
		}) => {
			courserWorksheet.addRow({
				symbol,
				boerse,
				basis_symbol,
				isin,
				faelligkeit,
				faelligkeit_f,
				sichtbar,
				land,
				waehrung,
				einheit,
				notierung,
				notierung_zusatz,
				name_deu,
				name_eng,
				name_zusatz,
				quelle
			});
		}
	);
	news.forEach(({ kategorie_id, kategorie, kategorie_eng, grusel, quelle }) => {
		newsWorkSheet.addRow({
			kategorie_id,
			kategorie,
			kategorie_eng,
			grusel,
			quelle
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Stammdaten--Benutzer-${userId}--${formatDate(new Date(), 'YYYY-MM-DD')}--${
		moment().hour() < 10 ? '0' + moment().hour() : moment().hour()
	}-${moment().minute() < 10 ? '0' + moment().minute() : moment().minute()}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportAdminListEntries = async (data: AdminMaterialListEntries[], listenId: number | null, listenName: string | undefined) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Kurse');
	worksheet.columns = adminListEntriesHeader;
	data.forEach((item: AdminMaterialListEntries) => {
		worksheet.addRow({
			symbol: item.symbol,
			boerse: item.boerse,
			basis_symbol: item.basis_symbol,
			isin: item.isin,
			faelligkeit: item.faelligkeit,
			faelligkeit_f: item.faelligkeit_f,
			frei: item.frei,
			sichtbar: item.sichtbar,
			land: item.land,
			waehrung: item.waehrung,
			einheit: item.einheit,
			name_deu: item.name_deu,
			name_eng: item.name_eng,
			kurszeit: item.kurszeit,
			letzter: item.letzter,
			settlement: item.settlement,
			geld: item.geld,
			brief: item.brief,
			hauptkursfeld_soll: item.hauptkursfeld_soll
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Kurse--${listenId}--${listenName}--${formatDate(new Date(), 'YYYY-MM-DD')}--${
		moment().hour() < 10 ? '0' + moment().hour() : moment().hour()
	}-${moment().minute() < 10 ? '0' + moment().minute() : moment().minute()}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportAdminListMasterData = async (data: AdminMasterData[], listenId: number | null, listenName: string | undefined) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Stammdaten');
	worksheet.columns = adminMasterDataHeaders;
	data.forEach((item: AdminMasterData) => {
		worksheet.addRow({
			symbol: item.symbol,
			boerse: item.boerse,
			basis_symbol: item.basis_symbol,
			isin: item.isin,
			faelligkeit: item.faelligkeit,
			faelligkeit_f: item.faelligkeit_f,
			frei: item.frei,
			sichtbar: item.sichtbar,
			editierbar_: item.editierbar_,
			land: item.land,
			waehrung: item.waehrung,
			waehrung_ignore: item.waehrung_ignore,
			einheit: item.einheit,
			notierung: item.notierung,
			notierung_zusatz: item.notierung_zusatz,
			nkstellen: item.nkstellen,
			max_kursalter: item.max_kursalter,
			name_deu: item.name_deu,
			name_eng: item.name_eng,
			name_zusatz: item.name_zusatz,
			boersenname: item.boersenname,
			datenlieferant: item.datenlieferant,
			quelle: item.quelle,
			kunde: item.kunde,
			hinzugefuegt_am: item.hinzugefuegt_am,
			entfernt_am: item.entfernt_am,
			lagerbestand_symbol: item.lagerbestand_symbol,
			lagerbestand_boerse: item.lagerbestand_boerse,
			info_de: item.info_de,
			info_en: item.info_en,
			priority: item.priority,
			verantwortlich: item.verantwortlich,
			prozent_abweichung_erlaubt: item.prozent_abweichung_erlaubt,
			aid_kommentar_: item.aid_kommentar_,
			link: item.link,
			notierung_zusatz_en: item.notierung_zusatz_en
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Stammdaten--${listenId}--${listenName}--${formatDate(new Date(), 'YYYY-MM-DD')}--${
		moment().hour() < 10 ? '0' + moment().hour() : moment().hour()
	}-${moment().minute() < 10 ? '0' + moment().minute() : moment().minute()}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const downloadDataAsCSV = async (columns: IExcelHeader[], res: ICSVData[], sheetName: string, myMaterialCourseLists: ICourseList) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(`${myMaterialCourseLists.listen_name}`);
	worksheet.columns = columns;
	res.map((object: any) => {
		worksheet.addRow(object);
	});
	const buf = await workbook.xlsx.writeBuffer();
	saveAs(new Blob([buf]), `${sheetName}.xlsx`);
};

export const exportMatfoxxOffersList = async (data: IMatfoxxOffersList[]) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet('Matfoxx--offers');
	worksheet.columns = matfoxxOffersListHeaders;
	data.forEach((item: IMatfoxxOffersList) => {
		worksheet.addRow({
			offer: item.offer,
			offer_eng: item.offer_eng,
			link: item.link,
			start_date: new Date(item.start_date),
			end_date: new Date(item.end_date)
		});
	});
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Matfoxx--offers-${formatDate(new Date(), 'YYYY-MM-DD')}--${
		moment().hour() < 10 ? '0' + moment().hour() : moment().hour()
	}-${moment().minute() < 10 ? '0' + moment().minute() : moment().minute()}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportMatfoxxJokesList = async (data: IMatfoxxJokesList[]) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(data[0].joke.replaceAll('\n', ' ').replaceAll(':', ' '), {
		pageSetup: { fitToPage: true, fitToHeight: 5, fitToWidth: 7 }
	});
	worksheet.columns = matfoxxJokesListHeaders;
	data.forEach((item: IMatfoxxJokesList) => {
		worksheet.addRow({
			joke: item.joke,
			joke_eng: item.joke,
			date: new Date(item.date)
		});
		const row = worksheet.lastRow;
		row?.eachCell((cell, colNumber) => {
			if (colNumber === 1) {
				const lines = cell?.value?.toString().split('\n').length;
				const height = (lines && lines * 15) || 15; // Adjust the height of perticular row
				row.height = height;
			}
		});
	});

	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Matfoxx--jokes-${formatDate(new Date(), 'YYYY-MM-DD')}--${
		moment().hour() < 10 ? '0' + moment().hour() : moment().hour()
	}-${moment().minute() < 10 ? '0' + moment().minute() : moment().minute()}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const exportNewsListJokesList = async (data: IAllNewsCategoriesList[]) => {
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(data[0].name.replaceAll('\n', ' ').replaceAll(':', ' '), {
		pageSetup: { fitToPage: true, fitToHeight: 5, fitToWidth: 7 }
	});
	worksheet.columns = newsListHeaders;
	data.forEach((item: IAllNewsCategoriesList) => {
		worksheet.addRow({
			kategorie_id: item.id,
			kategorie: item.name,
			kategorie_eng: item.name_eng,
			quelle: item.source,
			grusel: item.scary
		});
		const row = worksheet.lastRow;
		row?.eachCell((cell, colNumber) => {
			if (colNumber === 1) {
				const lines = cell?.value?.toString().split('\n').length;
				const height = (lines && lines * 15) || 15; // Adjust the height of perticular row
				row.height = height;
			}
		});
	});

	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Nachrichten-Rubriken--${formatDate(new Date(), 'YYYY-MM-DD')}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

// Backpack master data download in admin
export const exportBackpackMasterData = async (
	data: IAdminBackpackMasterData,
	language: string,
	id: number,
	name: string,
	t: TFunction<'translation', undefined, 'translation'>
) => {
	const workbook = new ExcelJS.Workbook();

	//Create four different work sheet
	const materilaWorkSheet = workbook.addWorksheet(language === 'en' ? 'Materials' : 'Materialien');
	const newsWorkSheet = workbook.addWorksheet(language === 'en' ? 'News' : 'Nachricht');
	const forecastSheet = workbook.addWorksheet(language === 'en' ? 'Forecasts' : 'Prognosen');
	const pdfModuleSheet = workbook.addWorksheet(language === 'en' ? 'PDFs' : 'PDFs');
	const userSheet = workbook.addWorksheet(language === 'en' ? 'Users' : 'Benutzer');
	const userApiSheet = workbook.addWorksheet('API');

	// Add headers to each and every worksheet
	userSheet.columns = userListBackpackMasterDataHeaders(t);
	forecastSheet.columns = forecastBackpackMasterDataHeaders(t);
	pdfModuleSheet.columns = pdfModuleBackpackMasterDataHeaders(t);
	newsWorkSheet.columns = newsBackpackMasterDataHeaders(t);
	materilaWorkSheet.columns = apiMasterDataCsvHeaders;
	userApiSheet.columns = apiMasterDataCsvHeaders;

	// add row to news list work sheet

	data?.newsList?.forEach((item: IBackpackNewsList) => {
		newsWorkSheet.addRow({
			group: language === 'en' && item.gruppe_eng !== '' ? item.gruppe_eng : item.gruppe,
			category: language === 'en' && item.kategorie_eng !== '' ? item.kategorie_eng : item.kategorie
		});
	});

	// add rows to user list work sheet
	data?.userList?.forEach((item: IBackpackUserList) => {
		userSheet.addRow({
			name: item.vorname,
			lastName: item.nachname,
			email: item.email
		});
	});

	//add rows to forecast list work sheet
	data?.forecastList?.forEach((item: IBackpackForecastList) => {
		forecastSheet.addRow({
			group: language === 'en' && item.group_name_eng !== '' ? item.group_name_eng : item.group_name,
			title: language === 'en' && item.titel !== '' ? item.titel_eng : item.titel
		});
	});

	//add rows to forecast list work sheet
	data?.pdfModuleLisz?.forEach((item: IBackpackPdfModuleList) => {
		pdfModuleSheet.addRow({
			group: language === 'en' && item.group_name_eng !== '' ? item.group_name_eng : item.group_name,
			title: language === 'en' && item.titel !== '' ? item.titel_eng : item.titel
		});
	});

	// add rows to material list work sheet
	data?.materialList?.forEach((item: IBakcpackMaterialList) => {
		materilaWorkSheet.addRow({
			symbol: item.symbol,
			boerse: item.boerse,
			basis_symbol: item.basis_symbol,
			isin: item.isin,
			faelligkeit: item.faelligkeit,
			faelligkeit_f: item.faelligkeit_f,
			sichtbar: item.sichtbar,
			land: item.land,
			waehrung: item.waehrung,
			einheit: item.einheit,
			notierung: item.notierung,
			notierung_zusatz: item.notierung_zusatz,
			name_deu: item.name_deu,
			name_eng: item.name_eng,
			name_zusatz: item.name_zusatz,
			quelle: item?.quelle
		});
	});

	data?.apimaterialList?.forEach((item: IBakcpackMaterialList) => {
		userApiSheet.addRow({
			symbol: item.symbol,
			boerse: item.boerse,
			basis_symbol: item.basis_symbol,
			isin: item.isin,
			faelligkeit: item.faelligkeit,
			faelligkeit_f: item.faelligkeit_f,
			sichtbar: item.sichtbar,
			land: item.land,
			waehrung: item.waehrung,
			einheit: item.einheit,
			notierung: item.notierung,
			notierung_zusatz: item.notierung_zusatz,
			name_deu: item.name_deu,
			name_eng: item.name_eng,
			name_zusatz: item.name_zusatz
		});
	});

	//set excel sheet name and download it
	const buf = await workbook.xlsx.writeBuffer();
	const downloadSheetName = `Stammdaten--${id}--${name}--${formatDate(new Date(), 'YYYY-MM-DD')}--${
		moment().hour() < 10 ? '0' + moment().hour() : moment().hour()
	}-${moment().minute() < 10 ? '0' + moment().minute() : moment().minute()}`;
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

type ITreeData = (
	| IAllMaterialType
	| IAllNewsType
	| IForecastsListType
	| IPdfModuleListType
	| IPdfModuleGroupData
	| IForecastGroupData
	| IMaterialGroupData
	| INewsGroupData
	| IAllCostModelType
)[];

//Filter in tree structure
const filterTreeSub = (treeData: ITreeData, searchValue: string, language: string): ITreeData => {
	const copyOfTreeData = [...treeData];
	return copyOfTreeData.filter((item) => {
		const name = handleNameKey((item as IAllMaterialType | IMaterialGroupData).name_deu, item.name_eng, item.name, language).toLowerCase();
		const isNameMatched = name.includes(searchValue.toLowerCase());
		if (isNameMatched) {
			return true;
		} else if (!isEmpty(item.children || [])) {
			const filteredItems = filterTreeSub(item.children || [], searchValue, language);
			item.children = filteredItems as any;
			return !isEmpty(filteredItems);
		} else {
			return false;
		}
	});
};

export const filterTree = (treeData: ITreeData, searchValue: string, data: ITreeData, language: string) => {
	const copyOfTreeData = [...treeData];
	if (searchValue !== '') {
		const newData = copyOfTreeData.filter((item) => {
			const name = handleNameKey((item as IAllMaterialType | IMaterialGroupData).name_deu, item.name_eng, item.name, language).toLowerCase();
			const isNameMatched = name.includes(searchValue.toLowerCase());
			if (isNameMatched) {
				return true;
			} else {
				const filteredItems = filterTreeSub(item.children || [], searchValue, language);
				item.children = filteredItems as any;
				return !isEmpty(filteredItems);
			}
		});
		return newData;
	} else {
		return data;
	}
};

export const handleArticleData = (data: string) => {
	const regex =
		/<(?!pre\b)(div|p|blockquote|hr|h[1-6]|section|article|aside|header|footer|nav)(?:\s+[^>]*)?>|<\/(?!pre\b)(div|p|blockquote|hr|h[1-6]|section|article|aside|header|footer|nav)>/gi;
	const result = data.replace(regex, '<p>');
	return result;
};

export const replaceHashtagFromUrl = (word: string) => {
	return word.includes('#') ? word.replace('#', '%23') : word;
};

export const calculateWeeklyAverage = (data: any, keys: string[]) => {
	return Object.values(data).map((value: any) => {
		return {
			...getAverageOfEntry(value, keys),
			date: moment(value.date).toDate(),
			week: value.week
		};
	});
};

export const calculateAverages = (stockData: any, notierung = '', chartType: string) => {
	if (stockData.length === 0) {
		return {
			dailyAverage: [],
			yearlyAverage: [],
			monthlyAverage: [],
			weeklyAverage: []
		};
	}

	stockData.sort((a: any, b: any) => a.date - b.date);

	const yearlyData: any = {};
	const monthlyData: any = {};
	const weeklyData: any = {};

	const keys = Array.from(new Set(stockData.flatMap((entry: IChartData) => Object.keys(entry).filter((key) => key.includes('cv'))))) as string[];
	const priceKeys = Array.from(
		new Set(stockData.flatMap((entry: IChartData) => Object.keys(entry).filter((key) => key.includes('price'))))
	) as string[];
	const percentageKeys = Array.from(
		new Set(stockData.flatMap((entry: IChartData) => Object.keys(entry).filter((key) => key.includes('percentage'))))
	) as string[];

	for (const entry of stockData) {
		const year = moment(entry.date).year();
		const month = moment(entry.date).month();
		const isoWeek = moment(entry.date).isoWeek();
		const yearMonth = `${year}-${month + 1}`;
		const tempDate = moment(entry.date).date();

		let yearWeek;
		if (tempDate >= 29 && isoWeek === 1) {
			yearWeek = `${year + 1}-${isoWeek}`;
		} else if (tempDate <= 3 && isoWeek >= 52) {
			yearWeek = `${year - 1}-${isoWeek}`;
		} else {
			yearWeek = `${year}-${isoWeek}`;
		}

		weeklyData[yearWeek] = {
			...getDynamicKeys(
				[...keys, ...priceKeys, ...percentageKeys],
				weeklyData[yearWeek],
				entry,
				isoWeek,
				notierung,
				chartType !== 'bar' ? true : false,
				chartType,
				stockData
			),
			date: weeklyData[yearWeek]?.date || entry.date,
			week: isoWeek
		};

		yearlyData[year] = {
			...getDynamicKeys([...keys, ...priceKeys, ...percentageKeys], yearlyData[year], entry, isoWeek, notierung, false, chartType, stockData),
			date: yearlyData[year]?.date || entry.date,
			week: isoWeek
		};

		monthlyData[yearMonth] = {
			...getDynamicKeys(
				[...keys, ...priceKeys, ...percentageKeys],
				monthlyData[yearMonth],
				entry,
				isoWeek,
				notierung,
				false,
				chartType,
				stockData
			),
			date: monthlyData[yearMonth]?.date || entry.date,
			week: isoWeek
		};
	}

	const yearlyAverage = calculateWeeklyAverage(yearlyData, [...keys, ...priceKeys, ...percentageKeys]);
	const monthlyAverage = calculateWeeklyAverage(monthlyData, [...keys, ...priceKeys, ...percentageKeys]);
	const weeklyAverage = calculateWeeklyAverage(weeklyData, [...keys, ...priceKeys, ...percentageKeys]);

	const dailyAverage = stockData.map((entry: any) => {
		const processedEntry: any = { date: new Date(entry.date) };
		[...keys, ...priceKeys, ...percentageKeys].forEach((key) => {
			if (key.includes('price') || key.includes('percentage')) {
				processedEntry[key] = entry[key];
			} else if (entry[key]) {
				processedEntry[key] = entry[key];
			}
		});
		return processedEntry;
	});

	return {
		dailyAverage,
		yearlyAverage,
		monthlyAverage,
		weeklyAverage
	};
};

export const calculateInicatorAverages = (stockData: any) => {
	if (stockData.length === 0) {
		return {
			dailyAverage: [],
			monthlyAverage: [],
			weeklyAverage: []
		};
	} else {
		// Sort the data by timestamp (if it's not already sorted)
		stockData.sort((a: any, b: any) => a.date - b.date);

		// Initialize variables to store aggregated data
		const monthlyData = {} as any;
		const weeklyData = {} as any;
		for (const entry of stockData) {
			// Calculate year, month, and ISO week for each entry
			const year = new Date(entry.datum).getFullYear();
			const month = new Date(entry.datum).getMonth();
			const isoWeek = moment(entry.datum).isoWeek();
			const yearMonth = `${year}-${month + 1}`;
			const tempDate = new Date(entry.datum).getDate();
			let yearWeek;
			if (tempDate >= 29 && isoWeek === 1) {
				yearWeek = `${year + 1}-${isoWeek}`;
			} else if (tempDate <= 3 && isoWeek >= 52) {
				yearWeek = `${year - 1}-${isoWeek}`;
			} else {
				yearWeek = `${year}-${isoWeek}`;
			}

			const currentWeeklyData = weeklyData[yearWeek];
			weeklyData[yearWeek] = {
				...entry,
				letzter: currentWeeklyData ? currentWeeklyData.letzter + entry?.letzter : entry?.letzter,
				count: currentWeeklyData ? currentWeeklyData.count + 1 : 1,
				datum: currentWeeklyData ? currentWeeklyData.datum : entry.datum,
				date: currentWeeklyData ? currentWeeklyData.date : `${year}/${isoWeek < 10 ? `0` + `${isoWeek}` : isoWeek}`
			};

			// Update the sums for each timeframe

			const currentMonthlyData = monthlyData[yearMonth];
			monthlyData[yearMonth] = {
				...entry,
				letzter: currentMonthlyData ? currentMonthlyData.letzter + entry.letzter : entry.letzter,
				count: currentMonthlyData ? currentMonthlyData.count + 1 : 1,
				datum: currentMonthlyData ? currentMonthlyData.datum : entry.datum,
				date: currentMonthlyData ? currentMonthlyData.date : `${year}/${month + 1 < 10 ? `0` + `${month + 1}` : month + 1}`
			};
		}

		// Calculate the averages
		const monthlyAverage = calculateIndicatorWeeklyAverage(monthlyData);
		const weeklyAverage = calculateIndicatorWeeklyAverage(weeklyData);
		const dailyAverage = stockData.map((entry: any) => {
			return {
				...entry,
				date: entry.datum
			};
		});

		return {
			monthlyAverage,
			weeklyAverage,
			dailyAverage
		};
	}
};

export const calculateIndicatorWeeklyAverage = (data: any) => {
	return Object.values(data).map((value: any) => {
		return {
			...value,
			letzter: value.letzter / value.count
		};
	});
};

const getDynamicKeys = (
	keys: string[],
	currentData: any,
	entry: any,
	isoWeek: number,
	notierung: string,
	isWeely: boolean,
	chartType: string,
	chartData?: any[]
) => {
	const obj: any = { ...currentData };
	obj._countMap = obj._countMap || {};
	obj._countMapActual = obj._countMapActual || {};

	for (const key of keys) {
		if (key.includes('price') || key.includes('percentage')) {
			const valueToAdd = entry[key] != null ? entry[key] : 0;
			obj[key] = (currentData?.[key] || 0) + valueToAdd;
			obj._countMap[key] = (obj._countMap[key] || 0) + (entry[key] != null ? 1 : 0);
			continue;
		}

		const entryData = entry[key];
		if (!entryData || !entryData.d) continue;

		const isForecasted = entryData.type === ForeCastType.FORECASTED_PRICE;
		const isActual = entryData.type === ForeCastType.ACTUAL_PRICE;
		const existingData = currentData?.[key];

		// 💡 Prefer actual if both forecast and actual exist
		const existingIsActual = existingData?.type === ForeCastType.ACTUAL_PRICE;

		if (isActual) {
			// Weekly actual-price handling: match entry.date with entryData.original_datum
			if (notierung === 'W' || isWeely) {
				// If any actual exists for this week/key, always use only actuals and ignore forecasts
				if (chartType !== 'bar') {
					obj[key] = {
						openSum: (existingData?.type === ForeCastType.ACTUAL_PRICE ? existingData?.openSum || 0 : 0) + (entryData.e || 0),
						highSum: (existingData?.type === ForeCastType.ACTUAL_PRICE ? existingData?.highSum || 0 : 0) + (entryData.h || 0),
						lowSum: (existingData?.type === ForeCastType.ACTUAL_PRICE ? existingData?.lowSum || 0 : 0) + (entryData.t || 0),
						closeSum: (existingData?.type === ForeCastType.ACTUAL_PRICE ? existingData?.closeSum || 0 : 0) + (entryData.l || 0),
						type: ForeCastType.ACTUAL_PRICE
					};
					obj._countMapActual[key] = (existingData?.type === ForeCastType.ACTUAL_PRICE ? obj._countMapActual[key] || 0 : 0) + 1;
				} else {
					if (entryData.original_datum && moment(entryData.original_datum).isSame(entry.date, 'day')) {
						obj[key] = {
							openSum: (existingData?.openSum || 0) + (entryData.e || 0),
							highSum: (existingData?.highSum || 0) + (entryData.h || 0),
							lowSum: (existingData?.lowSum || 0) + (entryData.t || 0),
							closeSum: (existingData?.closeSum || 0) + (entryData.l || 0),
							type: ForeCastType.ACTUAL_PRICE
						};
						obj._countMapActual[key] = (obj._countMapActual[key] || 0) + 1;
					}
				}
			} else if (moment(entry.date).isSame(entryData.date, 'day')) {
				// Default actual-price handling
				obj[key] = {
					openSum: (existingData?.openSum || 0) + (entryData.e || 0),
					highSum: (existingData?.highSum || 0) + (entryData.h || 0),
					lowSum: (existingData?.lowSum || 0) + (entryData.t || 0),
					closeSum: (existingData?.closeSum || 0) + (entryData.l || 0),
					type: ForeCastType.ACTUAL_PRICE
				};
				obj._countMapActual[key] = (obj._countMapActual[key] || 0) + 1;
			}
		} else if (isForecasted && entryData.source_data && !existingIsActual) {
			const monthlyValidSources = Object.entries(entryData.source_data).filter(([, source]: any) => !!source.original_datum);

			// Filter sources where original_datum matches entry.date
			const validSources =
				notierung === 'M'
					? monthlyValidSources
					: Object.entries(entryData.source_data).filter(([, source]: any) => {
							return !!source.original_datum && moment(source.original_datum).isSame(entry.date, 'day');
					  });

			if (validSources.length > 0) {
				const prevSourceData = existingData?.source_data || {};
				const newSourceData: any = { ...prevSourceData };

				for (const [sourceKey, forecastData] of validSources) {
					const prev = prevSourceData[sourceKey] || { openSum: 0, highSum: 0, lowSum: 0, closeSum: 0, count: 0 };

					// For monthly frequency, find the matching entry in chartData based on original_datum
					let dataToUse: any = forecastData;
					if (notierung === 'M' && chartData && (forecastData as any).original_datum) {
						const originalDate = (forecastData as any).original_datum;
						const matchingEntry = chartData.find((chartEntry: any) => moment(chartEntry.date).isSame(originalDate, 'day'));

						if (matchingEntry && matchingEntry[key] && matchingEntry[key].source_data && matchingEntry[key].source_data[sourceKey]) {
							// Use the data from the matching entry's source_data
							dataToUse = matchingEntry[key].source_data[sourceKey];
						}
					}

					newSourceData[sourceKey] = {
						openSum: prev.openSum + (dataToUse?.e || 0),
						highSum: prev.highSum + (dataToUse?.h || 0),
						lowSum: prev.lowSum + (dataToUse?.t || 0),
						closeSum: prev.closeSum + (dataToUse?.l || 0),
						count: prev.count + 1
					};
				}

				obj[key] = {
					type: ForeCastType.FORECASTED_PRICE,
					source_data: newSourceData
				};
				obj._countMap[key] = (obj._countMap[key] || 0) + 1;
			}
		}
	}

	obj.week = currentData?.week || isoWeek;
	return obj;
};

const getAverageOfEntry = (value: any, keys: string[]) => {
	const obj: any = {};
	const countMap = value._countMap || {};
	const countMapActual = value._countMapActual || {};

	for (const key of keys) {
		const keyCount = countMap[key] || 1;

		if (key.includes('price') || key.includes('percentage')) {
			obj[key] = value[key] != null ? value[key] / keyCount : null;
		} else {
			const priceData = value[key];
			if (!priceData) continue;

			const dataType = priceData.type || (priceData.source_data ? ForeCastType.FORECASTED_PRICE : ForeCastType.ACTUAL_PRICE);

			if (dataType === ForeCastType.FORECASTED_PRICE && priceData.source_data) {
				const avgSourceData: any = {};
				for (const sourceKey of Object.keys(priceData.source_data)) {
					const src = priceData.source_data[sourceKey];
					avgSourceData[sourceKey] = {
						e: src.openSum / src.count,
						h: src.highSum / src.count,
						l: src.closeSum / src.count,
						t: src.lowSum / src.count
					};
				}
				obj[key] = {
					type: ForeCastType.FORECASTED_PRICE,
					source_data: avgSourceData
				};
			} else if (dataType === ForeCastType.ACTUAL_PRICE) {
				const actualCount = countMapActual[key] || keyCount;
				obj[key] = {
					e: priceData.openSum / actualCount,
					h: priceData.highSum / actualCount,
					l: priceData.closeSum / actualCount,
					t: priceData.lowSum / actualCount,
					type: ForeCastType.ACTUAL_PRICE
				};
			}
		}
	}

	obj.date = value.date;
	obj.week = value.week;
	return obj;
};

export const getChartDataBasedOnSpan = (span: number, data: any) => {
	switch (span) {
		case 1:
			return data.dailyAverage;
		case 7:
			return data.weeklyAverage;
		case 30:
			return data.monthlyAverage;
		case 365:
			return data.yearlyAverage;
		default:
			return data.yearlyAverage;
	}
};

export const getChartDataBasedOnGroupBy = (span: string, data: any) => {
	switch (span) {
		case 'daily':
			return data.dailyAverage;
		case 'monthly':
			return data.monthlyAverage;
		case 'weekly':
			return data.weeklyAverage;
		default:
			return data.dailyAverage;
	}
};

export const renderTicks = (index: number, isBarChart: boolean, chartName: string) => {
	switch (index) {
		case 0:
		case 1:
			return {
				xTicks: isBarChart ? 7 : chartName === 'dashboard' ? 6 : 10,
				yTicks: 12
			};
		case 2:
		case 3: {
			return {
				xTicks: 3,
				yTicks: 3
			};
		}
		case 4:
		case 5:
		case 6: {
			return {
				xTicks: 3,
				yTicks: 2
			};
		}
		default:
			return {
				xTicks: 12,
				yTicks: 15
			};
	}
};

const getPriceTime = (date: string) => {
	const state = store.getState();
	const format = state.siteConfig.dateFormat;
	return moment(date).format(format || 'DD.MM.YYYY');
};
export const chartInfoTableData = (
	information: IChartInformation,
	language: string,
	admin: string[],
	t: TFunction<'translation', undefined, 'translation'>
) => {
	let currencyValue = '';
	if (information.waehrung === '' && information.einheit === '') {
		currencyValue = '-';
	} else if (information.waehrung === '') {
		currencyValue = `- / ${information.einheit}`;
	} else if (information.einheit === '') {
		currencyValue = `${information.waehrung} / -`;
	} else {
		currencyValue = `${information.waehrung} / ${information.einheit}`;
	}
	if (admin.length) {
		return [
			{
				label: `${t(localizationConstants.name)}`,
				value:
					(language === 'en' && information.name_eng !== '' && information.name_eng) ||
					(information.name_deu !== '' ? information.name_deu : information.name),
				Icon: NameIcon
			},
			{
				label: `${t(localizationConstants.category)}`,
				value: (!isEmpty(information.category) && information.category.join(' , ')) || '-',
				Icon: CategotyIcon
			},
			{
				label: `${t(localizationConstants.frequency)}`,
				value: chartUpdateFrequencyMapper(t, information.notierung) || '-',
				Icon: FrequencyIcon
			},
			{
				label: `${t(localizationConstants.original)} ${t(localizationConstants.currency)} / ${t(localizationConstants.unit)}`,
				value: currencyValue,
				Icon: CurrencyUnitIcon
			},
			{
				label: `${t(localizationConstants.stockExchange)}`,
				value: information.boersenname || '-',
				Icon: StockExchangeIcon
			},
			{
				label: `${t(localizationConstants.source)}`,
				value: information.quelle || '-',
				Icon: SourceIcon
			},
			{
				label: `${t(localizationConstants.dataAvailability)}`,
				value: `${information.oldestDate ? getPriceTime(information.oldestDate) : '-'} - ${
					information.newestDate ? getPriceTime(information.newestDate) : '-'
				}`,
				Icon: DataAvailabilityIcon
			},
			{
				label: `${t(localizationConstants.addInfo)}`,
				value: language === 'en' ? information.notierung_zusatz_en : information.notierung_zusatz || '-',
				Icon: AddInfoIcon
			},
			{
				label: `${t(localizationConstants.addedOn)}`,
				value: (information.hinzugefuegt_am && getPriceTime(information.hinzugefuegt_am)) || '-',
				Icon: AddedOnIcon
			},
			{
				label: `${t(localizationConstants.endedOn)}`,
				value: (information.entfernt_am && getPriceTime(information.entfernt_am)) || '-',
				Icon: EndedOnIcon
			}
		];
	} else {
		return [
			{
				label: `${t(localizationConstants.name)}`,
				value:
					(language === 'en' && information.name_eng !== '' && information.name_eng) ||
					(information.name_deu !== '' ? information.name_deu : information.name),
				Icon: NameIcon
			},
			{
				label: `${t(localizationConstants.category)}`,
				value: (!isEmpty(information.category) && information.category.join(' , ')) || '-',
				Icon: CategotyIcon
			},
			{
				label: `${t(localizationConstants.frequency)}`,
				value: chartUpdateFrequencyMapper(t, information.notierung) || '',
				Icon: FrequencyIcon
			},
			{
				label: `${t(localizationConstants.currency)} / ${t(localizationConstants.unit)}`,
				value: currencyValue,
				Icon: CurrencyUnitIcon
			},
			{
				label: `${t(localizationConstants.stockExchange)}`,
				value: information.boersenname || '-',
				Icon: StockExchangeIcon
			},
			{
				label: `${t(localizationConstants.dataAvailability)}`,
				value: `${information.oldestDate ? getPriceTime(information.oldestDate) : '-'} - ${
					information.newestDate ? getPriceTime(information.newestDate) : '-'
				}`,
				Icon: DataAvailabilityIcon
			},
			{
				label: `${t(localizationConstants.addInfo)}`,
				value: language === 'en' ? information.notierung_zusatz_en : information.notierung_zusatz || '-',
				Icon: AddInfoIcon
			},
			{
				label: `${t(localizationConstants.addedOn)}`,
				value: (information.hinzugefuegt_am && getPriceTime(information.hinzugefuegt_am)) || '-',
				Icon: AddedOnIcon
			},
			{
				label: `${t(localizationConstants.endedOn)}`,
				value: (information.entfernt_am && getPriceTime(information.entfernt_am)) || '-',
				Icon: EndedOnIcon
			}
		];
	}
};

export const barChartTickFormat = (d: number, data: any, span: string, t: TFunction<'translation', undefined, 'translation'>) => {
	if (span === '1d') {
		const date = data[d].date;
		return moment(date).format('DD.MM');
	} else if (span === '1w') {
		const tempDate = data[d].date.getDate();
		const year = data[d].date.getFullYear();
		const isoWeek = data[d].week;
		let updatedYear;
		if (tempDate >= 29 && isoWeek === 1) {
			updatedYear = year + 1;
		} else if (tempDate <= 3 && isoWeek >= 52) {
			updatedYear = year - 1;
		} else {
			updatedYear = year;
		}
		if (data[d].week === 1) {
			return `${moment(data[d].date).format('MMM')} ${updatedYear}`;
		}
		return `${t(localizationConstants.week)} ${data[d].week}`;
	} else if (span === '1y') {
		return moment(data[d].date).year();
	} else {
		return ''; // You can customize this part as needed
	}
};

export const germanNumberFormattter = (number: number, decimalPoint = 2) => {
	// Get decimal_separator from siteConfig state
	const state = store.getState();
	const decimalSeparator = state.siteConfig.decimal_separator || 'de';

	// Configure formatLocale based on decimal_separator
	const numberFormatter = formatLocale({
		decimal: decimalSeparator === 'de' ? ',' : '.',
		thousands: decimalSeparator === 'de' ? '.' : ',',
		grouping: [3],
		currency: ['', '']
	});
	const formatter = `,.${decimalPoint}f`;
	const num = numberFormatter.format(formatter)(number);
	const splitNumber = num.split(decimalSeparator === 'de' ? '.' : ',');
	if (splitNumber[1] === '00') {
		return numberFormatter.format(',.0f')(number);
	} else {
		return numberFormatter.format(formatter)(number);
	}
};

export const defineChartMargins = (chartSettings: IChartSettingsType[], isOnDashboard: boolean) => {
	const totalLeftAxis = filter(chartSettings, { axis: { value: ChartAxisPosition.LEFT_AXIS } }).length;
	const totalRightAxis = filter(chartSettings, { axis: { value: ChartAxisPosition.RIGHT_AXIS } }).length;
	if (totalLeftAxis > 0 && totalRightAxis > 0) {
		return { left: 80, right: 80 };
	} else if (totalLeftAxis > 0 && totalRightAxis === 0) {
		return { left: isOnDashboard ? 65 : 80, right: isOnDashboard ? 9 : 0 };
	} else if (totalRightAxis > 0 && totalLeftAxis === 0) {
		return { left: isOnDashboard ? 9 : 0, right: isOnDashboard ? 65 : 80 };
	}
};

export const chartTimeRangeMapper = (chartData: any): { [key: string]: number } => {
	if (!isEmpty(chartData)) {
		return {
			'1d': 1,
			'1w': 7,
			'1m': 30,
			'3m': 90,
			'6m': 180,
			'1y': 365,
			'3y': 1095,
			'5y': 1825,
			'10y': 3650,
			ytd: getDifferenceDaysForOneYear(new Date(chartData[chartData.length - 1].date)),
			max: chartData.length
		};
	} else {
		return {};
	}
};

export const chartTimeRangeMapperForNumber = (chartData: any, span: string) => {
	if (parseInt(span) === 7) {
		return '1w';
	} else if (parseInt(span) === 30) {
		return '1m';
	} else if (parseInt(span) === 90) {
		return '3m';
	} else if (parseInt(span) === 180) {
		return '6m';
	} else if (parseInt(span) === 365) {
		return '1y';
	} else if (parseInt(span) === 1095) {
		return '3y';
	} else if (parseInt(span) === 1825) {
		return '5y';
	} else if (parseInt(span) === 3650) {
		return '10y';
	} else if (chartData.length === parseInt(span)) {
		return 'max';
	} else if (
		span === '1d' ||
		span === '1w' ||
		span === '1m' ||
		span === '3m' ||
		span === '6m' ||
		span === '1y' ||
		span === '3y' ||
		span === '5y' ||
		span === '10y' ||
		span === 'ytd' ||
		span === 'max'
	) {
		return span;
	} else {
		return '1y';
	}
};

export const getStartDateForExport = (span: string, endDate: string, chartData: any) => {
	if (span === '1d') {
		return moment(endDate).subtract(1, 'day').format();
	} else if (span === '1w') {
		return moment(endDate).subtract(1, 'week').format();
	} else if (span === '1m') {
		return moment(endDate).subtract(1, 'month').format();
	} else if (span === '3m') {
		return moment(endDate).subtract(3, 'months').format();
	} else if (span === '6m') {
		return moment(endDate).subtract(6, 'months').format();
	} else if (span === '1y') {
		return moment(endDate).subtract(1, 'year').format();
	} else if (span === '3y') {
		return moment(endDate).subtract(3, 'years').format();
	} else if (span === '5y') {
		return moment(endDate).subtract(5, 'years').format();
	} else if (span === '10y') {
		return moment(endDate).subtract(10, 'years').format();
	} else if (span === 'ytd') {
		if (isEmpty(chartData)) {
			return moment(endDate).startOf('year').format();
		} else {
			return moment(chartData[chartData.length - 1].date)
				.startOf('year')
				.format();
		}
	} else if (span === 'max') {
		return chartData[0].date;
	}
};

export const handleTenderDate = (inputDateString: string, format: string) => {
	const date = moment(inputDateString);
	const formattedDate = date.format(format); // format the date part
	const timePart = inputDateString.slice(11, 19); // get HH:mm:ss as is
	return `${formattedDate} ${timePart}`;
};

export const exportFutureTableData = async (firstTableData: string[][], secondTableData: string[]) => {
	const workbook = new ExcelJS.Workbook();
	const hour = moment().hour();
	const minute = moment().minute();
	const downloadSheetName = `Kurse--${formatDate(new Date(), 'YYYY-MM-DD')}--${hour < 10 ? '0' + hour : hour}-${
		minute < 10 ? '0' + minute : minute
	}`;
	const worksheet = workbook.addWorksheet(downloadSheetName);
	worksheet.addRows(firstTableData);
	worksheet.addRow([]);
	worksheet.addRows(secondTableData);
	worksheet.columns = [
		{ width: 30 }, // Width for column A
		{ width: 30 }, // Width for column B
		{ width: 30 } // Width for column C
		// Add more columns and widths as needed
	];
	const buf = await workbook.xlsx.writeBuffer();
	saveAs(new Blob([buf]), `${downloadSheetName}.xlsx`);
};

export const getDifferenceInMinutes = (startTime: string, endTime: string) => {
	const date1 = new Date(startTime).getTime();
	const date2 = new Date(endTime).getTime();
	const diffInMilliseconds = Math.abs(date1 - date2);
	return Math.floor(diffInMilliseconds / (1000 * 60));
};

export const splitAlertOptions = (word: string) => {
	return word.split(/(?=[+/])/g); // Splits but keeps delimiters attached to subsequent values
};

export const getSeletectedOptionForAlert = (categories: string[], dropdownOptions: IDropDownOption[]) => {
	const AND_OPERATOR = t(localizationConstants.andOperator);
	const OR_OPERATOR = t(localizationConstants.orOperator);
	return categories
		.map((id, index) => {
			const cleanedId = id.replace(/[+/]/g, ''); // Remove '+' and '/' for lookup
			const option = dropdownOptions.find((opt) => opt.value === cleanedId);

			if (!option) return null;

			// Identify and replace the delimiter in `id` with the localized equivalent
			const matchedDelimiter = id.match(/[+/]/g)?.[0] || ''; // Get the first matched delimiter
			const localizedDelimiter = matchedDelimiter === '+' ? AND_OPERATOR : matchedDelimiter === '/' ? OR_OPERATOR : '';

			return {
				label: option.label,
				value: index === 0 ? cleanedId : `${localizedDelimiter}${cleanedId}`
			};
		})
		.filter((option): option is { value: string; label: string } => option !== null); // Remove null values
};

export const getKeywordOptions = (data: string) => {
	let wordOptions: { value: string; label: string }[] = [];
	if (data.trim() !== '') {
		const AND_OPERATOR = t(localizationConstants.andOperator);
		const OR_OPERATOR = t(localizationConstants.orOperator);

		const delimiters = ['+', '/'];
		const regex = new RegExp(`[${delimiters.join('')}]`);

		wordOptions = data
			.split(regex)
			.map((word, index) => {
				if (!word.trim()) return null;

				const matchedDelimiter = data.match(/[+/]/g)?.[index - 1] || '';
				const localizedDelimiter = matchedDelimiter === '+' ? AND_OPERATOR : matchedDelimiter === '/' ? OR_OPERATOR : '';
				return {
					value: `${localizedDelimiter}${word}`,
					label: word
				};
			})
			.filter((option): option is { value: string; label: string } => option !== null);
	}
	return wordOptions;
};

export const filterAuthorization = (treeData: ITreeData, key: string, obj: any) => {
	const copyOfTreeData = [...treeData];
	const newData = copyOfTreeData.filter((item) => {
		const material = obj[`${item.id}${item.type}`];

		if (material) {
			if (key === 'isForecastLocked') {
				if (material[key] && material['hasForecast']) {
					return true;
				}
			} else if (material[key]) {
				return true;
			}
		}

		if (material && item.children && !isEmpty(item.children || [])) {
			const filteredItems = filterTreeSubAuth(item.children || [], key, material.children);
			item.children = filteredItems as any;
			return !isEmpty(filteredItems);
		}
		return false;
	});
	return newData;
};

const filterTreeSubAuth = (treeData: ITreeData, key: string, obj: any): ITreeData => {
	const copyOfTreeData = [...treeData];
	return copyOfTreeData.filter((item) => {
		const material = obj[`${item.id}${item.type}`];

		if (material) {
			if (key === 'isForecastLocked') {
				if (material[key] && material['hasForecast']) {
					return true;
				}
			} else if (material[key]) {
				return true;
			}
		}

		if (material && item.children && !isEmpty(item.children || [])) {
			const filteredItems = filterTreeSubAuth(item.children || [], key, material.children);
			item.children = filteredItems as any;
			return !isEmpty(filteredItems);
		}
		return false;
	});
};

export const filterAuthorizationCostModel = (treeData: CostModelGroupData[], key: string, obj: any) => {
	const copyOfTreeData = [...treeData];
	const newData = copyOfTreeData.filter((item) => {
		const material = obj[`${item.id}${item.type}`];

		if (material) {
			if (material[key]) {
				return true;
			}
		}

		if (material && item.children && !isEmpty(item.children || [])) {
			const filteredItems = filterTreeSubCostModelAuth(item.children || [], key, material.children);
			item.children = filteredItems as any;
			return !isEmpty(filteredItems);
		}
		return false;
	});
	return newData;
};

const filterTreeSubCostModelAuth = (treeData: CostModelGroupData[], key: string, obj: any): CostModelGroupData[] => {
	const copyOfTreeData = [...treeData];
	return copyOfTreeData.filter((item) => {
		const material = obj[`${item.id}${item.type}`];

		if (material) {
			if (material[key]) {
				return true;
			}
		}

		if (material && item.children && !isEmpty(item.children || [])) {
			const filteredItems = filterTreeSubCostModelAuth(item.children || [], key, material.children);
			item.children = filteredItems as any;
			return !isEmpty(filteredItems);
		}
		return false;
	});
};

export const handleCopyText = (text: string) => {
	(navigator.clipboard || {})
		.writeText(text)
		.then(() => {
			notify(i18n.t(localizationConstants.copyToClipboard), 'success');
		})
		.catch(() => {
			notify(i18n.t(localizationConstants.somethingWentWrong));
		});
};

export const processString = (input: string) => {
	// Special case: If the input exactly matches '*/<word>/*' or '*+<word>+*', extract the word
	const match = input.match(/^\*[/+](.+?)[/+]\*$/);
	if (match) {
		return match[1]; // Return only the extracted word
	}

	// Step 1: Remove * before and after words
	let result = input
		.replace(/(\w)\*+/g, '$1') // Remove * after a word
		.replace(/\*+(\w)/g, '$1'); // Remove * before a word

	// Step 2: Replace `/` or `+` with space
	result = result.replace(/[/+]/g, ' ');

	// Step 3: Merge words with * when applicable (without spaces)
	result = result.replace(/\b\s*\*\s*\b/g, '*');

	// Step 4: If last character is '*', ensure it's only one '*'
	result = result.replace(/\s*\*+\s*$/, '*');

	// Step 5: Remove * if it's the first character
	result = result.replace(/^\*/, '');

	return result.trim();
};

export const sanitizeSheetName = (name: string) => {
	return name.replace(/[*?:\\/[\]]/g, '_'); // replaces each invalid char with a dash
};

export const isAdminToken = (token: string): string[] => {
	if (!token) return [];

	try {
		// Decode JWT token (format: header.payload.signature)
		const parts = token.split('.');
		if (parts.length !== 3) {
			return []; // Not a valid JWT
		}

		// Decode the payload (second part)
		const payload = JSON.parse(atob(parts[1]));

		// Check if admin field exists and has admin privileges
		if (payload.admin && Array.isArray(payload.admin)) {
			// Return the admin array
			return payload.admin;
		}

		return [];
	} catch (error) {
		// Token is not JWT or invalid
		console.error('Error decoding JWT token:', error);
		return [];
	}
};

// Helper function to clean forecast item names by removing -1, -2, -3 suffixes
export const cleanForecastItemName = (itemName: string): string => {
	return itemName.replace(/-\d+$/, '');
};

// Helper function to create date format without year
export const getDateFormatWithoutYear = (format: string) => {
	// Remove year part from the format
	return format
		.replace(/yyyy/g, '')
		.replace(/YYYY/g, '')
		.replace(/yy/g, '')
		.replace(/YY/g, '')
		.replace(/\.yyyy/g, '')
		.replace(/\.YYYY/g, '')
		.replace(/\/yyyy/g, '')
		.replace(/\/YYYY/g, '')
		.replace(/\.yy/g, '')
		.replace(/\.YY/g, '')
		.replace(/\/yy/g, '')
		.replace(/\/YY/g, '')
		.replace(/\.$/, '') // Remove trailing dot
		.replace(/\/$/, '') // Remove trailing slash
		.replace(/^\./, '') // Remove leading dot
		.replace(/^\//, ''); // Remove leading slash
};

/**
 * Parse date string in multiple formats (DD.MM.YYYY, MM.DD.YYYY, YYYY.MM.DD)
 * @param dateString - The date string to parse
 * @returns Date object if valid, null if invalid
 */
export const parseDateString = (dateString: string): Date | null => {
	if (!dateString || dateString.length !== 10) {
		return null;
	}

	// Remove any non-digit and non-dot characters
	const cleanString = dateString.replace(/[^\d.]/g, '');

	if (cleanString.length !== 10) {
		return null;
	}

	const parts = cleanString.split('.');
	if (parts.length !== 3) {
		return null;
	}

	const [part1, part2, part3] = parts.map((part) => parseInt(part, 10));

	// Check if any part is NaN
	if (isNaN(part1) || isNaN(part2) || isNaN(part3)) {
		return null;
	}

	// Try different format combinations
	const formats = [
		// DD.MM.YYYY
		{ day: part1, month: part2, year: part3 },
		// MM.DD.YYYY
		{ day: part2, month: part1, year: part3 },
		// YYYY.MM.DD
		{ day: part3, month: part2, year: part1 }
	];

	for (const format of formats) {
		// Validate year range (reasonable range)
		if (format.year < 1900 || format.year > 2100) {
			continue;
		}

		// Validate month range
		if (format.month < 1 || format.month > 12) {
			continue;
		}

		// Validate day range
		if (format.day < 1 || format.day > 31) {
			continue;
		}

		// Create date and validate it's actually valid
		const date = new Date(format.year, format.month - 1, format.day);

		// Check if the date is valid (handles edge cases like 31.02.2025)
		if (date.getFullYear() === format.year && date.getMonth() === format.month - 1 && date.getDate() === format.day) {
			return date;
		}
	}

	return null;
};

export const hexToRgba = (hex: string, alpha: number) => {
	const bigint = parseInt(hex.replace('#', ''), 16);
	const r = (bigint >> 16) & 255;
	const g = (bigint >> 8) & 255;
	const b = bigint & 255;
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const calculateAveragesCostModel = (stockData: any) => {
	if (!stockData || stockData.length === 0) {
		return {
			dailyAverage: [],
			yearlyAverage: [],
			monthlyAverage: [],
			weeklyAverage: []
		};
	}

	const avgKeys = ['close', 'low', 'high', 'percentChange'];

	// Helper to group and average
	const groupAndAverage = (groupFn: (entry: any) => string) => {
		const groups: { [key: string]: { sum: any; count: number; date: any; week?: number } } = {};
		stockData.forEach((entry: any) => {
			const groupKey = groupFn(entry);
			if (!groups[groupKey]) {
				groups[groupKey] = { sum: {}, count: 0, date: entry.date, week: moment(entry.date).isoWeek() };
				avgKeys.forEach((key) => (groups[groupKey].sum[key] = 0));
			}
			avgKeys.forEach((key) => {
				groups[groupKey].sum[key] += entry[key] ?? 0;
			});
			groups[groupKey].count += 1;
		});
		return Object.entries(groups).map(([key, { sum, count, date, week }]) => {
			const avg: any = { date: new Date(date) };
			if (week) avg.week = week;
			avgKeys.forEach((k) => {
				avg[k] = count ? sum[k] / count : null;
			});
			return avg;
		});
	};

	// Grouping functions
	const getYear = (entry: any) => `${moment(entry.date).year()}`;
	const getMonth = (entry: any) => {
		const m = moment(entry.date);
		return `${m.year()}-${m.month() + 1}`;
	};
	const getWeek = (entry: any) => {
		const m = moment(entry.date);
		const year = m.year();
		const week = m.isoWeek();
		const tempDate = m.date();
		let yearWeek;
		if (tempDate >= 29 && week === 1) {
			yearWeek = `${year + 1}-${week}`;
		} else if (tempDate <= 3 && week >= 52) {
			yearWeek = `${year - 1}-${week}`;
		} else {
			yearWeek = `${year}-${week}`;
		}
		return yearWeek;
	};

	// Calculate averages
	const yearlyAverage = groupAndAverage(getYear);
	const monthlyAverage = groupAndAverage(getMonth);
	const weeklyAverage = groupAndAverage(getWeek);

	// Daily is just the data itself, but ensure date is a Date object
	const dailyAverage = stockData.map((entry: any) => ({
		...entry,
		date: new Date(entry.date)
	}));

	return {
		dailyAverage,
		yearlyAverage,
		monthlyAverage,
		weeklyAverage
	};
};

export const getAdjustedDimensions = (originalWidth: number, originalHeight: number, maxWidth: number, maxHeight: number) => {
	if (!originalWidth || !originalHeight) {
		return { width: maxWidth, height: maxHeight };
	}

	const widthRatio = maxWidth / originalWidth;
	const heightRatio = maxHeight / originalHeight;
	const scale = Math.min(widthRatio, heightRatio); // pick the smaller ratio

	return {
		width: Math.round(originalWidth * scale),
		height: Math.round(originalHeight * scale)
	};
};
// Helper function to map data type keys to their display names
export const getDataTypeDisplayName = (key: string): string => {
	const dataTypeMap: { [key: string]: string } = {
		e: 'erster',
		h: 'hoch',
		t: 'tief',
		l: 'letzter',
		o: 'oi',
		s: 'settlement',
		g: 'geld',
		b: 'brief',
		u: 'umsatz',
		v: 'volumen'
	};
	return dataTypeMap[key] || key;
};

// Helper function to map display names back to data type keys
export const getDataTypeKey = (displayName: string): string => {
	const reverseDataTypeMap: { [key: string]: string } = {
		erster: 'e',
		hoch: 'h',
		tief: 't',
		letzter: 'l',
		oi: 'o',
		settlement: 's',
		geld: 'g',
		brief: 'b',
		umsatz: 'u',
		volumen: 'v'
	};
	return reverseDataTypeMap[displayName] || displayName;
};

// Helper function to get data type value from chart item
export const getDataTypeValue = (chartItem: IChartItem, dataType: string): number | null => {
	switch (dataType) {
		case 'e':
			return chartItem.l;
		case 'h':
			return chartItem.l;
		case 't':
			return chartItem.l;
		case 'l':
			return chartItem.l;
		case 'o':
			return chartItem.l;
		case 's':
			return chartItem.l; // settlement maps to 'p' property
		case 'g':
			return (chartItem as any).l; // gried
		case 'b':
			return (chartItem as any).l; // brief
		case 'u':
			return (chartItem as any).l; // umsatz
		case 'v':
			return (chartItem as any).l; // volumen
		default:
			return null;
	}
};

// Function to get color for each data type, matching tooltip logic
export const getColorForDataType = (baseColor: string, dataTypeIndex: number, selectedDataTypes: string[]) => {
	const shouldShowMultipleDataTypes = selectedDataTypes && selectedDataTypes.length > 0;
	if (shouldShowMultipleDataTypes) {
		const opacity = (100 - (dataTypeIndex + 1) * 10) / 100;
		return hexToRgba(baseColor, opacity);
	}
	return baseColor;
};

export const exchangeRate = (item: string, t: TFunction<'translation', undefined, 'translation'>) => {
	switch (item) {
		case 'default':
			return `${t(localizationConstants.forex)}`;

		case 'lme':
			return `${t(localizationConstants.lme)}`;

		case 'Bloomberg':
			return `${t(localizationConstants.bloomberg)}`;

		case 'ecb':
			return `${t(localizationConstants.ecb)}`;

		default:
			return item; // or return '' if you want a fallback
	}
};

export const getDisplayField = (fields: string[] = [], originalField: string): string => {
	// If no fields selected, return empty string (default case handled by originalField)
	if (!fields || fields.length === 0) return '';

	// If only one field is selected
	if (fields.length === 1) {
		// If the only selected field is the original field, show nothing (default case)
		if (fields[0] === originalField) return '';
		// Otherwise show the selected field
		return fields[0];
	}

	// If multiple fields are selected
	if (fields.length > 1) {
		// If settlement is not selected, return the first field in the selected fields array
		if (fields.includes(originalField)) {
			return originalField;
		} else {
			return fields[0];
		}
	}
	// Default case: return empty string
	return '';
};
